import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LucideIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

interface BeratungDetailLayoutProps {
  icon: LucideIcon;
  title: string;
  intro: string;
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  children: ReactNode;
}

const BeratungDetailLayout = ({
  icon: Icon,
  title,
  intro,
  seoTitle,
  seoDescription,
  canonical,
  children,
}: BeratungDetailLayoutProps) => (
  <>
    <SEOHead title={seoTitle} description={seoDescription} canonical={canonical} />
    <Header />
    <main className="py-20 md:py-28">
      <div className="container max-w-3xl">
        <Link
          to="/portfolio/beratung"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zur Beratung
        </Link>

        <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
          <motion.div
            variants={fadeInUp}
            className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary"
          >
            <Icon className="h-7 w-7" />
          </motion.div>
          <motion.h1
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-5"
          >
            {title}
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground leading-relaxed mb-12">
            {intro}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="space-y-10"
        >
          {children}
        </motion.div>
      </div>
    </main>
    <Footer />
  </>
);

export const BeratungSection = ({ heading, children }: { heading: string; children: ReactNode }) => (
  <motion.section variants={fadeInUp}>
    <h2 className="text-2xl font-serif font-semibold text-foreground mb-3">{heading}</h2>
    <div className="text-muted-foreground leading-relaxed space-y-3">{children}</div>
  </motion.section>
);

export default BeratungDetailLayout;
